// ai/pipelines/inject-brand-logo.ts
//
// Locate every Logo element in a slide subtree, pick the user's
// brand logo from the design system (Brandfetch snapshot) themed to
// the closest non-transparent ancestor background, and inject the
// resolved URL into the entity's `settings.src`.
//
// Runs AFTER the rewrite pipeline so Logo elements skip the AI image
// pass entirely (the rewrite pipeline's collectRewriteTargets explicitly
// excludes them). Logos must always come from the user's actual brand,
// not from Wikipedia or Unsplash.

import { produce } from "immer";
import isDarkColor from "@providers/custom-dnd-provider/hooks/use-invert-color/utils/is-dark-color";
import { isLogoSlotImage } from "./logo-slot";
import { isKeptPicture } from "./kept-picture";

export type BrandLogoSnapshot = {
  logos?: {
    theme?: "dark" | "light";
    type?: string;
    formats?: { format?: string; src?: string }[];
  }[];
};

// Brandfetch's `theme` describes the LOGO's color, not the background
// it's intended for:
//   theme="dark"  = dark-colored logo → use on LIGHT backgrounds
//   theme="light" = light/white logo  → use on DARK backgrounds
// Strict: only return a logo whose theme is appropriate for the
// requested background, otherwise we'd paint e.g. a white logo on a
// white slide.
export const pickBrandLogoUrl = (
  brandSnapshot: BrandLogoSnapshot | null | undefined,
  isDarkBackground: boolean,
): string | null => {
  const logos = brandSnapshot?.logos;
  if (!logos || logos.length === 0) return null;
  const desiredTheme = isDarkBackground ? "light" : "dark";
  const sameTheme = logos.filter((l) => l.theme === desiredTheme);
  if (sameTheme.length === 0) return null;
  const match =
    sameTheme.find((l) => l.type === "logo") ??
    sameTheme.find((l) => l.type === "symbol") ??
    sameTheme[0];
  const formats = match?.formats ?? [];
  if (formats.length === 0) return null;
  const png = formats.find(
    (f) => f.format === "png" || f.format === "webp" || f.format === "jpeg",
  );
  const svg = formats.find((f) => f.format === "svg");
  return (png?.src ?? svg?.src ?? formats[0]?.src) || null;
};

export const resolveCssColor = (
  raw: unknown,
  designSystem: Record<string, unknown> | null | undefined,
): string | null => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  if (["transparent", "none", "inherit"].includes(raw.trim().toLowerCase()))
    return null;
  const varMatch = raw.match(/^var\(--([a-z0-9-]+)\)$/i);
  if (!varMatch) return raw;
  const cssVar = varMatch[1].toLowerCase();
  // The two whose variable name does not simply camel-case to their key.
  const aliased =
    cssVar === "background-main-color"
      ? "backgroundColor"
      : cssVar === "background-secondary-color"
        ? "backgroundColor2"
        : null;
  // EVERY OTHER THEME COLOUR, not just the two backgrounds.
  //
  // `--theme-color-3` is what a template paints its dark slides with, and
  // returning null for it meant "no resolvable background", which is read as
  // a light one -- so a deck put its DARK logo on a near-black slide. The
  // variable names are the design-system keys in kebab case, so they convert.
  const key = aliased ?? cssVar.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());
  const value = designSystem?.[key];
  return typeof value === "string" && value ? value : null;
};

// Walks the slide tree, finds the first Logo element, and resolves the
// closest non-transparent ancestor backgroundColor. Logos are commonly
// nested inside a section/combo whose bg differs from the slide root
// (e.g. a white header strip on top of a hero image).
export const detectLogoBackgroundIsDark = (
  slide: unknown,
  designSystem: Record<string, unknown> | null | undefined,
  fallbackIsDark: boolean,
): boolean => {
  let result: string | null = null;
  const visit = (
    node: unknown,
    ancestorBg: string | null,
    siblings: unknown[],
  ): boolean => {
    if (!node || typeof node !== "object") return false;
    const n = node as Record<string, unknown>;
    const settings = n.settings as Record<string, unknown> | undefined;
    const ownBg = resolveCssColor(settings?.backgroundColor, designSystem);
    const currentBg = ownBg ?? ancestorBg;
    if (
      n.type === "element" &&
      ((typeof settings?.name === "string" &&
        (settings.name as string).toLowerCase() === "logo") ||
        // The mark carried as a small Image beside the heading; see ./logo-slot.
        isLogoSlotImage(settings, siblings))
    ) {
      result = currentBg;
      return true;
    }
    if (Array.isArray(n.children))
      for (const c of n.children)
        if (visit(c, currentBg, n.children)) return true;
    return false;
  };
  visit(slide, null, []);
  if (result) return isDarkColor(result);
  // No Logo element found, or no resolvable bg up the chain.
  // Default to light so we don't paint a white-on-white logo.
  return fallbackIsDark;
};

export const injectBrandLogoIntoSlide = (
  slide: unknown,
  logoUrl: string,
): unknown => {
  if (!logoUrl || !slide || typeof slide !== "object") return slide;
  return produce(slide as Record<string, unknown>, (draft) => {
    const visit = (node: unknown, siblings: unknown[]) => {
      if (!node || typeof node !== "object") return;
      const n = node as Record<string, unknown>;
      const settings = n.settings as Record<string, unknown> | undefined;
      if (
        n.type === "element" &&
        settings &&
        typeof settings.name === "string" &&
        settings.name.toLowerCase() === "logo"
      ) {
        settings.src = logoUrl;
      } else if (
        n.type === "element" &&
        isLogoSlotImage(settings, siblings) &&
        // A mark the team uploaded is theirs to keep (./kept-picture).
        !isKeptPicture(settings)
      ) {
        // A mark in an Image frame: contained, not cropped. See ./logo-slot.
        (settings as Record<string, unknown>).src = logoUrl;
        (settings as Record<string, unknown>).shape = "free";
      }
      if (Array.isArray(n.children))
        for (const c of n.children) visit(c, n.children);
    };
    visit(draft, []);
  });
};
